// Shared auth types and permission matrix.
// Demo and real providers should both implement this surface area so the UI stays stable.

export type UserRole = 'owner' | 'admin' | 'manager' | 'viewer' | 'super_admin'
export type OrgTier = 'free' | 'pro' | 'business'

export type Permission =
  | 'create_drop'
  | 'edit_drop'
  | 'delete_drop'
  | 'view_drops'
  | 'manage_team'
  | 'manage_billing'
  | 'view_analytics'
  | 'view_settings'
  | 'delete_account'
  | 'super_admin'

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
}

export interface Organization {
  id: string
  name: string
  tier: OrgTier
  role: UserRole
  isPlatformAdmin?: boolean
  pendingDowngrade?: {
    toTier: OrgTier
    effectiveDate: number
    requestedAt: number
  }
}

export interface Session {
  expiresAt: number
  createdAt: number
}

export interface AuthContextType {
  isLoaded: boolean
  isSignedIn: boolean
  user: User | null
  organization: Organization | null
  session: Session | null
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
  signInWithMagicLink: (email: string) => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<void>
  signOut: () => Promise<void>
  updateUserName: (name: string) => Promise<void>
  updateUserAvatar: (file: File | null) => Promise<void>
  setUserRole: (role: UserRole) => void
  setOrgTier: (tier: OrgTier) => void
  requestDowngrade: (toTier: OrgTier, gracePeriodDays?: number) => Promise<void>
  cancelDowngrade: () => Promise<void>
  hasPermission: (permission: Permission) => boolean
  hasRole: (roles: UserRole | UserRole[]) => boolean
  simulateExpiredSession: () => void
}

export const rolePermissions: Record<UserRole, Permission[]> = {
  owner: [
    'create_drop',
    'edit_drop',
    'delete_drop',
    'view_drops',
    'manage_team',
    'manage_billing',
    'view_analytics',
    'view_settings',
    'delete_account',
  ],
  admin: [
    'create_drop',
    'edit_drop',
    'delete_drop',
    'view_drops',
    'manage_team',
    'view_analytics',
    'view_settings',
  ],
  manager: [
    'create_drop',
    'edit_drop',
    'delete_drop',
    'view_drops',
    'view_analytics',
    'view_settings',
  ],
  viewer: [
    'view_drops',
  ],
  super_admin: [
    'create_drop',
    'edit_drop',
    'delete_drop',
    'view_drops',
    'manage_team',
    'manage_billing',
    'view_analytics',
    'view_settings',
    'delete_account',
    'super_admin',
  ],
}

